import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import API, { isAxiosError } from 'utils/api';
import { PlanType } from '../utils/plans';
import { Frequency } from '../components/Pricing/plans';

export type AccessFee = {
  amount: string;
  currency: string;
  /** Paid, or waived for a grandfathered shop. */
  settled: boolean;
  /**
   * Settled, but the plan it unlocks is not active — the merchant approved the fee
   * and left before approving the usage subscription, so they need a way to finish.
   */
  awaitingActivation: boolean;
};

export type UseSubscriptionDetailsResponse = {
  subscriptionDetails: {
    planType: PlanType;
    frequency?: Frequency;
    currentPeriodEnd: string;
    isStripeSubscriber: boolean;
    isShopifySubscriber: boolean;
    trialDaysRemaining: number;
    /**
     * Served by the backend rather than hardcoded here, so the amount shown and the
     * amount collected cannot drift apart.
     */
    accessFee?: AccessFee;
  };
};

async function getSubscriptionDetails(): Promise<UseSubscriptionDetailsResponse | null> {
  try {
    const response = await API().get('/shop/me/subscription');
    return response.data;
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 401) {
      return null;
    }
    throw e;
  }
}

export default function useSubscriptionDetails(
  options?: Omit<
    UseQueryOptions<UseSubscriptionDetailsResponse | null>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<UseSubscriptionDetailsResponse | null> {
  return useQuery({
    queryKey: ['subscriptionDetails'],
    queryFn: getSubscriptionDetails,
    retry: false,
    ...options,
  });
}
